import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import {
  Lightbulb, Plus, ThumbsUp, ThumbsDown, MessageSquare, Pin, X,
  Loader2, ArrowLeft, Send, Trash2, Sparkles, ChevronDown, ShieldAlert,
  Image as ImageIcon, Upload, CheckCircle2, User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  ideasList, ideasGet, ideasCreate, ideasVote, ideasComment,
  ideasDeleteOwn, ideasDeleteOwnComment,
  ideasUploadScreenshot, ideaScreenshotUrl,
  ideasAdminPatch, ideasAdminDelete, ideasAdminDeleteComment,
  Idea, IdeaComment, IdeaStatus,
} from '../api'

const MAX_SHOTS_IDEA = 5
const MAX_SHOTS_COMMENT = 3

const CATEGORIES: Record<string, string> = {
  panel: 'Панель',
  antidetect: 'Антидетект',
  proxy: 'Прокси',
  vpn: 'VPN',
  chrome: 'Chrome',
  captcha: 'Капча',
  docs: 'Документация',
  api: 'API',
  other: 'Другое',
  future: 'На будущее',  // когда-нибудь сделаем — backlog для долгосрочных идей
}

const STATUS_META: Record<IdeaStatus, { label: string; cls: string }> = {
  voting:      { label: 'Голосование',  cls: 'bg-primary/10 text-primary border-primary/30' },
  in_progress: { label: 'В работе',     cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  done:        { label: 'Готово',       cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  rejected:    { label: 'Отклонено',    cls: 'bg-red-500/10 text-red-400 border-red-500/30' },
}

const SORT_OPTIONS = [
  { id: 'popular',   label: 'Популярные' },
  { id: 'new',       label: 'Новые' },
  { id: 'discussed', label: 'Обсуждаемые' },
  { id: 'old',       label: 'Старые' },
]

function formatDate(ts: number): string {
  try {
    const d = new Date(ts * 1000)
    const now = Date.now()
    const diff = (now - d.getTime()) / 1000
    if (diff < 60) return 'только что'
    if (diff < 3600) return `${Math.floor(diff / 60)} мин. назад`
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч. назад`
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}


// ── Screenshot uploader & viewer ──────────────────────────────────

export function ScreenshotUploader({ value, onChange, max, disabled }: {
  value: string[]   // stored filenames
  onChange: (v: string[]) => void
  max: number
  disabled?: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setErr('')
    const remaining = max - value.length
    if (remaining <= 0) { setErr(`Максимум ${max} скриншотов`); return }
    const list = Array.from(files).slice(0, remaining)
    setBusy(true)
    const added: string[] = []
    for (const f of list) {
      if (!f.type.startsWith('image/')) { setErr('Можно загружать только изображения'); continue }
      if (f.size > 5 * 1024 * 1024) { setErr(`Файл ${f.name} больше 5 MB`); continue }
      const r = await ideasUploadScreenshot(f)
      if (r.filename) added.push(r.filename)
      else if (r.error) setErr(r.error)
    }
    if (added.length) onChange([...value, ...added])
    setBusy(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const remove = (name: string) => {
    onChange(value.filter(x => x !== name))
  }

  const atLimit = value.length >= max

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {value.map(name => (
          <div key={name} className="relative group w-20 h-20 rounded-md overflow-hidden border border-border bg-muted/20">
            <img src={ideaScreenshotUrl(name)} alt="" className="w-full h-full object-cover" />
            {!disabled && (
              <button onClick={() => remove(name)}
                      className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-red-500/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                      title="Удалить">
                <X className="w-3 h-3 text-white" />
              </button>
            )}
          </div>
        ))}
        {!disabled && !atLimit && (
          <button type="button" onClick={() => inputRef.current?.click()}
                  disabled={busy}
                  className="w-20 h-20 rounded-md border-2 border-dashed border-border hover:border-primary/40 hover:bg-muted/30 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="text-[9px] leading-tight">{busy ? 'загрузка' : `${value.length}/${max}`}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple={max > 1}
             className="hidden" onChange={e => void handleFiles(e.target.files)} />
      {err && <div className="text-[11px] text-red-400">{err}</div>}
      {!err && value.length === 0 && !disabled && (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> PNG/JPG до 5 MB, максимум {max}
        </div>
      )}
    </div>
  )
}


export function ScreenshotGallery({ names, small }: { names?: string[]; small?: boolean }) {
  const [open, setOpen] = useState<string | null>(null)
  if (!names || names.length === 0) return null
  const size = small ? 'w-16 h-16' : 'w-24 h-24'
  return (
    <>
      <div className={`flex flex-wrap gap-1.5`}>
        {names.map(n => (
          <button key={n} onClick={() => setOpen(n)}
                  className={`${size} rounded-md overflow-hidden border border-border bg-muted/20 hover:ring-2 hover:ring-primary/40 transition`}>
            <img src={ideaScreenshotUrl(n)} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-6"
             onClick={() => setOpen(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
                  onClick={() => setOpen(null)}>
            <X className="w-5 h-5" />
          </button>
          <img src={ideaScreenshotUrl(open)} alt=""
               className="max-w-[95vw] max-h-[90vh] rounded-lg shadow-2xl"
               onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  )
}

interface IdeasPageProps {
  selectedId?: number | null
  onSelectChange?: (id: number | null) => void
  onTitleResolved?: (title: string) => void
  isSuperAdmin?: boolean
  // Когда true — создание идей запрещено для лицензии (с лиц-сервера).
  restricted?: boolean
}

/** Same idea as BugsPage's `renderBugAuthor` — shown verbatim because
 *  Bugs and Ideas live in different files but share the rendering rule:
 *  super-admins see real `panel_username` + clickable subdomain URL,
 *  everyone else sees the user-typed nickname (or "анон"). */
function renderIdeaAuthor(item: any, isSuperAdmin: boolean): React.ReactNode {
  const c = item?.client
  if (isSuperAdmin && c) {
    const username = (c.panel_username || '').trim()
    const sub = username ? `${username}.crp.pro` : (c.hostname || '').trim()
    return (
      <span className="inline-flex flex-wrap items-center gap-x-1">
        {username && <span className="text-primary font-medium">@{username}</span>}
        {!username && sub && <span className="text-primary font-medium">{sub}</span>}
        {sub && username && (
          <a href={`https://${sub}`} target="_blank" rel="noreferrer"
             className="text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
             onClick={(e) => e.stopPropagation()}
             title="Открыть панель клиента">
            {sub}
          </a>
        )}
        {item.author_label && (
          <span className="text-muted-foreground italic">"{item.author_label}"</span>
        )}
      </span>
    )
  }
  return <>{item.author_label || 'анон'}</>
}

export default function IdeasPage({ selectedId: controlledId, onSelectChange, onTitleResolved, isSuperAdmin = false, restricted = false }: IdeasPageProps = {}) {
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>('voting')
  const [filterCategory, setFilterCategory] = useState<string>('')
  const [sort, setSort] = useState<string>('popular')
  // Idea #79: "Мои" — show only ideas authored by the current client.
  // When active we drop the status filter so the user sees ALL their ideas
  // (across statuses), then filter to is_mine on the client.
  const [mine, setMine] = useState(false)
  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(null)
  const selectedId = controlledId !== undefined ? controlledId : internalSelectedId
  const setSelectedId = useCallback((id: number | null) => {
    if (onSelectChange) onSelectChange(id)
    else setInternalSelectedId(id)
  }, [onSelectChange])
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await ideasList({
        status: mine ? undefined : (filterStatus || undefined),
        category: filterCategory || undefined,
        sort,
      })
      setIdeas(r.ideas || [])
    } catch { /* ignore */ }
    setLoading(false)
  }, [filterStatus, filterCategory, sort, mine])

  useEffect(() => { void load() }, [load])

  const displayed = mine ? ideas.filter(i => i.is_mine) : ideas

  const onVote = async (id: number, value: -1 | 0 | 1) => {
    try {
      const r = await ideasVote(id, value)
      setIdeas(cur => cur.map(x => x.id === id ? { ...x, votes_up: r.votes_up, votes_down: r.votes_down, score: r.votes_up - r.votes_down, my_vote: r.my_vote } : x))
    } catch { /* ignore */ }
  }

  if (selectedId !== null) {
    return <IdeaDetailPage id={selectedId} onBack={() => { setSelectedId(null); void load() }} onTitleResolved={onTitleResolved} isSuperAdmin={isSuperAdmin} />
  }

  return (
    <div className="p-5 max-w-5xl">
      <div className="mb-4">
        <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-primary" />
          Идеи и предложения
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Голосуйте за идеи, предлагайте свои, обсуждайте в комментариях.
          Приоритет разработки определяется по количеству голосов.
        </p>
        {!restricted && (
          <div className="mt-3">
            <Button onClick={() => setCreating(true)} className="gap-2">
              <Plus className="w-4 h-4" /> Предложить идею
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setMine(m => !m)}
          title="Показать только мои идеи"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition-colors ${
            mine
              ? 'bg-primary/15 border-primary/40 text-primary'
              : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/30'
          }`}
        >
          <User className="w-3.5 h-3.5" /> Мои
        </button>
        <Select
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder="Голосование"
          options={Object.entries(STATUS_META).map(([id, m]) => ({ id, label: m.label }))}
        />
        <Select
          value={filterCategory}
          onChange={setFilterCategory}
          placeholder="Все категории"
          options={[
            { id: '', label: 'Все категории' },
            ...Object.entries(CATEGORIES).map(([id, label]) => ({ id, label })),
          ]}
        />
        <div className="ml-auto">
          <Select value={sort} onChange={setSort} options={SORT_OPTIONS} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Sparkles className="w-12 h-12 mx-auto opacity-30 mb-3" />
          <p className="text-lg mb-2">{mine ? 'У вас пока нет идей' : 'Пока нет идей'}</p>
          <p className="text-sm">{mine ? 'Здесь появятся идеи, которые вы предложили.' : 'Будьте первым — предложите что улучшить!'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {displayed.map(idea => (
            <IdeaCard key={idea.id} idea={idea}
                      onClick={() => setSelectedId(idea.id)}
                      onVote={onVote}
                      isSuperAdmin={isSuperAdmin} />
          ))}
        </div>
      )}

      {creating && <NewIdeaModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void load() }} />}
    </div>
  )
}


function IdeaCard({ idea, onClick, onVote, isSuperAdmin = false }: {
  idea: Idea
  onClick: () => void
  onVote: (id: number, value: -1 | 0 | 1) => void
  isSuperAdmin?: boolean
}) {
  const sm = STATUS_META[idea.status]
  const isDone = idea.status === 'done'
  return (
    <div onClick={onClick}
         className={`rounded-xl border p-4 space-y-2 transition-colors cursor-pointer relative overflow-hidden ${
           isDone
             ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.06] to-card hover:border-emerald-500/50'
             : 'border-border bg-card hover:border-primary/40'
         }`}>
      {isDone && (
        <CheckCircle2 className="absolute bottom-3 right-3 w-8 h-8 text-emerald-500/20" />
      )}
      {idea.pinned && !isDone && (
        <Pin className="absolute top-3 right-3 w-3.5 h-3.5 text-primary rotate-45" />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={`text-[10px] h-5 ${sm.cls}`}>{sm.label}</Badge>
        <span className="text-[10px] text-muted-foreground">
          #{CATEGORIES[idea.category] || idea.category}
        </span>
        {(idea as any).has_team_reply && (
          <Badge variant="outline" className="text-[10px] h-5 border-primary/40 text-primary bg-primary/10">
            ✓ Ответ команды
          </Badge>
        )}
      </div>
      <h3 className="text-sm font-semibold line-clamp-2">{idea.title}</h3>
      {idea.description && (
        <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
          {idea.description}
        </p>
      )}
      {idea.screenshots && idea.screenshots.length > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <ImageIcon className="w-3 h-3" />
          <span>{idea.screenshots.length}</span>
        </div>
      )}
      {isSuperAdmin && (
        <div className="text-[10px] text-muted-foreground pt-0.5 truncate">
          {renderIdeaAuthor(idea, true)}
        </div>
      )}
      <div className="flex items-center gap-3 pt-1 text-xs">
        <button
          onClick={(e) => { e.stopPropagation(); onVote(idea.id, idea.my_vote === 1 ? 0 : 1) }}
          className={`flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
            idea.my_vote === 1
              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
              : 'border-border hover:border-emerald-500/30 hover:text-emerald-400'
          }`}
        >
          <ThumbsUp className="w-3.5 h-3.5" /> {idea.votes_up}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onVote(idea.id, idea.my_vote === -1 ? 0 : -1) }}
          className={`flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
            idea.my_vote === -1
              ? 'bg-red-500/20 border-red-500/40 text-red-400'
              : 'border-border hover:border-red-500/30 hover:text-red-400'
          }`}
        >
          <ThumbsDown className="w-3.5 h-3.5" /> {idea.votes_down}
        </button>
        {idea.comments_count > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground ml-auto">
            <MessageSquare className="w-3.5 h-3.5" /> {idea.comments_count}
          </span>
        )}
      </div>
    </div>
  )
}


function IdeaDetailPage({ id, onBack, onTitleResolved, isSuperAdmin = false }: {
  id: number; onBack: () => void; onTitleResolved?: (t: string) => void; isSuperAdmin?: boolean
}) {
  const [idea, setIdea] = useState<Idea | null>(null)
  const [comments, setComments] = useState<IdeaComment[]>([])
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState('')
  const [replyShots, setReplyShots] = useState<string[]>([])
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await ideasGet(id)
      setIdea(r.idea)
      setComments(r.comments)
      if (onTitleResolved && r.idea?.title) onTitleResolved(r.idea.title)
    } catch { /* ignore */ }
    setLoading(false)
  }, [id, onTitleResolved])

  useEffect(() => { void load() }, [load])

  const onVote = async (value: -1 | 0 | 1) => {
    if (!idea) return
    try {
      const r = await ideasVote(idea.id, value)
      setIdea(cur => cur ? { ...cur, votes_up: r.votes_up, votes_down: r.votes_down, score: r.votes_up - r.votes_down, my_vote: r.my_vote } : cur)
    } catch { /* ignore */ }
  }

  const onSend = async () => {
    const t = replyText.trim()
    if ((!t && replyShots.length === 0) || !idea) return
    setSending(true)
    try {
      await ideasComment(idea.id, t || '(скриншот)', undefined, replyShots)
      setReplyText('')
      setReplyShots([])
      await load()
    } catch { /* ignore */ }
    setSending(false)
  }

  const onDeleteComment = async (cid: number) => {
    if (!confirm('Удалить комментарий?')) return
    try {
      await ideasDeleteOwnComment(cid)
      setComments(cur => cur.filter(c => c.id !== cid))
    } catch { /* ignore */ }
  }

  const onDeleteIdea = async () => {
    if (!idea) return
    if (!confirm('Удалить свою идею? Скрыть для всех?')) return
    try {
      await ideasDeleteOwn(idea.id)
      onBack()
    } catch { /* ignore */ }
  }

  if (loading || !idea) {
    return (
      <div className="p-5 max-w-5xl flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const sm = STATUS_META[idea.status]

  return (
    <div className="p-5 max-w-5xl space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
        <ArrowLeft className="w-4 h-4" /> Назад к списку
      </Button>

      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="flex items-start gap-2 flex-wrap">
          <Badge variant="outline" className={`${sm.cls}`}>{sm.label}</Badge>
          <span className="text-xs text-muted-foreground py-0.5">
            #{CATEGORIES[idea.category] || idea.category}
          </span>
          {idea.pinned && <Pin className="w-4 h-4 text-primary rotate-45" />}
          <span className="text-xs text-muted-foreground ml-auto inline-flex items-center gap-1">
            {renderIdeaAuthor(idea, isSuperAdmin)}
            <span className="opacity-50">·</span>
            <span>{formatDate(idea.created_at)}</span>
          </span>
        </div>
        <h1 className="text-xl font-semibold tracking-tight">{idea.title}</h1>
        {idea.description && (
          <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
            {idea.description}
          </p>
        )}
        {idea.screenshots && idea.screenshots.length > 0 && (
          <div className="pt-1">
            <ScreenshotGallery names={idea.screenshots} />
          </div>
        )}
        <div className="flex items-center gap-3 pt-2">
          <button onClick={() => onVote(idea.my_vote === 1 ? 0 : 1)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors ${
                    idea.my_vote === 1
                      ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                      : 'border-border hover:border-emerald-500/30 hover:text-emerald-400'
                  }`}>
            <ThumbsUp className="w-4 h-4" /> {idea.votes_up}
          </button>
          <button onClick={() => onVote(idea.my_vote === -1 ? 0 : -1)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors ${
                    idea.my_vote === -1
                      ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : 'border-border hover:border-red-500/30 hover:text-red-400'
                  }`}>
            <ThumbsDown className="w-4 h-4" /> {idea.votes_down}
          </button>
          <span className="ml-auto text-xs text-muted-foreground">
            Итого: <span className={idea.score > 0 ? 'text-emerald-400' : idea.score < 0 ? 'text-red-400' : ''}>
              {idea.score > 0 ? '+' : ''}{idea.score}
            </span>
          </span>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-semibold text-amber-300">Действия модератора</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Статус:</span>
            {(Object.keys(STATUS_META) as IdeaStatus[]).map(s => (
              <button key={s}
                onClick={async () => {
                  if (s === idea.status) return
                  try {
                    await ideasAdminPatch(idea.id, { status: s } as any)
                    setIdea(cur => cur ? { ...cur, status: s } : cur)
                  } catch { /* noop */ }
                }}
                className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                  s === idea.status
                    ? STATUS_META[s].cls + ' ring-1 ring-current'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/40'
                }`}
              >{STATUS_META[s].label}</button>
            ))}
          </div>
          {/* Категория — для перемещения в «На будущее» и обратно */}
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-amber-500/20">
            <span className="text-[11px] text-muted-foreground">Категория:</span>
            {Object.entries(CATEGORIES).map(([cid, label]) => (
              <button key={cid}
                onClick={async () => {
                  if (cid === idea.category) return
                  try {
                    await ideasAdminPatch(idea.id, { category: cid } as any)
                    setIdea(cur => cur ? { ...cur, category: cid } : cur)
                  } catch { /* noop */ }
                }}
                className={`text-[10px] px-2 py-1 rounded-md border transition-colors ${
                  cid === idea.category
                    ? 'bg-primary/20 border-primary/40 text-primary ring-1 ring-current'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/40'
                }`}
              >{label}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-amber-500/20">
            <button
              onClick={async () => {
                try {
                  await ideasAdminPatch(idea.id, { pinned: !idea.pinned } as any)
                  setIdea(cur => cur ? { ...cur, pinned: !cur.pinned } : cur)
                } catch { /* noop */ }
              }}
              className="text-[11px] px-2 py-1 rounded-md border border-border hover:border-primary/40 hover:text-primary"
            >
              {idea.pinned ? 'Открепить' : 'Закрепить'}
            </button>
            <button
              onClick={async () => {
                try {
                  await ideasAdminPatch(idea.id, { hidden: !(idea as any).hidden } as any)
                  setIdea(cur => cur ? { ...cur, hidden: !(cur as any).hidden } as any : cur)
                } catch { /* noop */ }
              }}
              className="text-[11px] px-2 py-1 rounded-md border border-border hover:border-amber-500/40 hover:text-amber-400"
            >
              {(idea as any).hidden ? 'Показать всем' : 'Скрыть от всех'}
            </button>
            <button
              onClick={async () => {
                if (!confirm(`Удалить идею #${idea.id} НАВСЕГДА? Это действие необратимо.`)) return
                try {
                  await ideasAdminDelete(idea.id)
                  onBack()
                } catch { /* noop */ }
              }}
              className="ml-auto text-[11px] px-2 py-1 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10"
            >
              Удалить навсегда
            </button>
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Комментарии ({comments.length})
        </h2>

        {comments.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">Пока нет обсуждений. Оставьте первый комментарий.</p>
        )}

        <div className="space-y-2">
          {comments.map(c => (
            <div key={c.id}
                 className={`rounded-lg border p-3 text-sm ${
                   c.is_admin
                     ? 'bg-primary/5 border-primary/30'
                     : 'bg-muted/30 border-border'
                 }`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                {c.is_admin ? (
                  <Badge variant="outline" className="h-5 text-[10px] bg-primary/15 text-primary border-primary/30">Команда</Badge>
                ) : (
                  <span className="font-medium text-foreground/80">{renderIdeaAuthor(c, isSuperAdmin)}</span>
                )}
                <span>·</span>
                <span>{formatDate(c.created_at)}</span>
                {c.is_mine && (
                  <button onClick={() => onDeleteComment(c.id)}
                          className="ml-auto text-red-400 hover:text-red-300" title="Удалить">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
                {!c.is_mine && isSuperAdmin && (
                  <button onClick={async () => {
                            if (!confirm('Удалить этот комментарий?')) return
                            try {
                              await ideasAdminDeleteComment(c.id)
                              setComments(cur => cur.filter(x => x.id !== c.id))
                            } catch { /* noop */ }
                          }}
                          className="ml-auto text-amber-400 hover:text-amber-300" title="Удалить (модератор)">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="whitespace-pre-wrap leading-relaxed">{c.text}</div>
              {c.screenshots && c.screenshots.length > 0 && (
                <div className="mt-2">
                  <ScreenshotGallery names={c.screenshots} small />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-2">
          <div className="flex gap-2">
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Напишите комментарий..."
              rows={2}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[60px]"
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void onSend() }
              }}
            />
            <Button onClick={onSend} disabled={(!replyText.trim() && replyShots.length === 0) || sending} className="self-end gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Отправить
            </Button>
          </div>
          <ScreenshotUploader value={replyShots} onChange={setReplyShots} max={MAX_SHOTS_COMMENT} disabled={sending} />
          <p className="text-[11px] text-muted-foreground">Ctrl+Enter — быстро отправить</p>
        </div>
      </div>

      {/* Own delete */}
      <div className="flex justify-end pt-2">
        <Button variant="ghost" size="sm" onClick={onDeleteIdea}
                className="text-xs text-muted-foreground hover:text-red-400 gap-1.5">
          <Trash2 className="w-3 h-3" /> Удалить свою идею
        </Button>
      </div>
    </div>
  )
}


function NewIdeaModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<string>('other')
  const [authorLabel, setAuthorLabel] = useState('')
  const [shots, setShots] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const onSubmit = async () => {
    if (!title.trim()) return
    setError('')
    setSending(true)
    try {
      const r = await ideasCreate({
        title: title.trim(),
        description: description.trim(),
        category,
        author_label: authorLabel.trim(),
        screenshots: shots,
      })
      if (r.ok) {
        onCreated()
      } else {
        setError(r.error || 'Не удалось создать идею')
      }
    } catch (e: any) {
      setError(e?.message || 'Ошибка сети')
    }
    setSending(false)
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-card border border-border rounded-xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" /> Новая идея
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Заголовок <span className="text-red-400">*</span></label>
          <Input value={title} onChange={e => setTitle(e.target.value)}
                 placeholder="Короткое описание идеи" maxLength={200} />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Описание</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
                    rows={4} maxLength={4000}
                    placeholder="Подробнее о том что хочется улучшить или добавить..."
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-y min-h-[100px]" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Категория</label>
            <Select value={category} onChange={setCategory}
                    options={Object.entries(CATEGORIES).map(([id, label]) => ({ id, label }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Ваше имя <span className="text-muted-foreground">(опц.)</span></label>
            <Input value={authorLabel} onChange={e => setAuthorLabel(e.target.value)}
                   placeholder="Как подписать" maxLength={60} />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium">Скриншоты <span className="text-muted-foreground">(до {MAX_SHOTS_IDEA}, опц.)</span></label>
          <ScreenshotUploader value={shots} onChange={setShots} max={MAX_SHOTS_IDEA} disabled={sending} />
        </div>

        {error && <div className="text-xs text-red-400">{error}</div>}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={onSubmit} disabled={!title.trim() || sending} className="gap-2">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Предложить
          </Button>
        </div>
      </div>
    </div>
  )
}


// ── Mini select component (без shadcn Select чтобы не тащить dropdown deps) ─

function Select({ value, onChange, options, placeholder }: {
  value: string
  onChange: (v: string) => void
  options: { id: string; label: string }[]
  placeholder?: string
}) {
  return (
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)}
              className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-background text-sm cursor-pointer hover:border-primary/30">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
    </div>
  )
}
